import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { EntityInfo, ParticipantInfo } from '@mtm/task-ui-angular';
import type { DriveItem } from '@mtm/drive/core';
import { UserService } from './user.service';

import { ApiService } from './api.service';
import { EntityParticipant, EntityParticipantsService } from './entity-participants.service';
import { DriveProjectStructureService } from './drive-project-structure.service';
import { EntityItemNameResolverService } from './entity-item-name-resolver.service';
import { DriveSurfaceService } from './drive-surface.service';
import { PlatformRbacSignatureService, SignatureRequestResult } from './platform-rbac-signature.service';
import { ProjectService } from './project.service';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';
import { mapDirectoryKindToEntityType } from '../drive/drive-entity-type.const';
import type { Project } from '../interfaces';
import type { WorkspaceMode } from '../interfaces/workspace.interface';

/** `ParticipantInfo` plus the IAM account uuid, which the task UI ignores but drive mentions need. */
export interface PlatformParticipant extends ParticipantInfo {
  accountUuid?: string;
}

interface EntityItem {
  entityUuid: string;
  entityType: string;
  entityName: string;
  entityPath?: string;
  hasChildren?: boolean;
  children?: EntityItem[] | null;
  additionalProperties?: { mode?: WorkspaceMode };
}

interface EntitiesApiResponse {
  message?: string;
  data?: EntityItem;
}

export interface SignatureResponse {
  access_token?: string;
  expires_in?: number;
}

/**
 * Drive sub-entity types (below PROJECT) that live in drive/storage, not local Cassandra. Used to
 * flag participants with `?drive=true` and to scope signature requests with `projectId` (STUDIO-1808).
 */
export const DRIVE_PROJECT_SCOPED_ENTITY_TYPES: ReadonlySet<string> = new Set([
  PLATFORM_CHAT_ENTITY_TYPE.SECTION,
  PLATFORM_CHAT_ENTITY_TYPE.SUB_SECTION,
  PLATFORM_CHAT_ENTITY_TYPE.FOLDER,
  PLATFORM_CHAT_ENTITY_TYPE.SUB_FOLDER,
  PLATFORM_CHAT_ENTITY_TYPE.VERSION,
  PLATFORM_CHAT_ENTITY_TYPE.FILE,
  PLATFORM_CHAT_ENTITY_TYPE.PAGE,
]);

@Injectable({ providedIn: 'root' })
export class PlatformTaskDataService {
  private readonly apiService = inject(ApiService);
  private readonly entityParticipants = inject(EntityParticipantsService);
  private readonly userService = inject(UserService);
  private readonly nameResolver = inject(EntityItemNameResolverService);
  private readonly rbacSignature = inject(PlatformRbacSignatureService);
  private readonly projectService = inject(ProjectService);
  private readonly driveStructure = inject(DriveProjectStructureService);
  private readonly driveSurface = inject(DriveSurfaceService);

  private readonly signatureRequests = new Map<string, Observable<SignatureRequestResult>>();

  /**
   * Drive nodes discovered while traversing the entity tree, keyed by the emitted `entityUuid`
   * (`elementUuid` for dirs, project id for roots). Value = the Drive `directoryUuid` to list for
   * this node's children (`null` until resolved for a project root).
   */
  private readonly driveNodeRegistry = new Map<string, string | null>();

  getParticipants(entityType: string, entityUuid: string): Observable<PlatformParticipant[]> {
    // Drive sub-entities live in drive/storage, not local Cassandra. Pass drive:true so the backend
    // resolves the owning project's participants while keeping the requested identity (STUDIO-1808).
    // Without the flag, drive uuids hit the legacy local path and fail.
    // The type alone is not enough: the same SECTION/FILE type exists in legacy projects, where it
    // is a local entity with no owning project to resolve. Pair it with the surface.
    const drive: boolean = DRIVE_PROJECT_SCOPED_ENTITY_TYPES.has(entityType) && this.driveSurface.isDriveSurface();
    return this.entityParticipants.getParticipants(entityType, entityUuid, drive).pipe(
      map((participants: EntityParticipant[]) =>
        participants.map((p: EntityParticipant) => ({
          username: p.username,
          ...(p.accountUuid ? { accountUuid: p.accountUuid } : {}),
          firstName: p.firstName,
          lastName: p.lastName,
          avatarUrl: this.userService.getAvatarUrl(p.username),
        }))
      )
    );
  }

  getEntities(entityType: string, entityUuid: string, deep: number = 1): Observable<EntityInfo[]> {
    if (this.isDriveEntity(entityType, entityUuid)) {
      return this.getDriveEntities(entityUuid);
    }
    return this.apiService.httpGet(`/api/v1/entities/${entityType}/${entityUuid}?deep=${deep}`).pipe(
      map((res: EntitiesApiResponse) => {
        if (!res?.data) {
          return [];
        }
        this.registerDriveNodes(res.data);
        return [res.data as EntityInfo];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * A node is drive-sourced when it was already discovered as one (registry), or it is the
   * currently-loaded drive-mode project. Every drive descendant is registered as it is emitted,
   * so a later lazy-load of that node resolves here without re-querying.
   */
  private isDriveEntity(entityType: string, entityUuid: string): boolean {
    if (this.driveNodeRegistry.has(entityUuid)) {
      return true;
    }
    const project: Project | null = this.projectService.projectOnloaded ? this.projectService.project : null;
    return entityType === PLATFORM_CHAT_ENTITY_TYPE.PROJECT && project?.mode === 'drive' && project.id === entityUuid;
  }

  /**
   * Register drive project roots found in a legacy response — a `PROJECT` node marked
   * `additionalProperties.mode === 'drive'` — and force its expand arrow. The legacy API reports
   * `hasChildren: false` for a drive project (its contents live in Drive), which would hide the
   * chevron; we override it so the user can drill into the Drive-sourced tree. Only the project
   * boundary switches to Drive; WORKSPACE/CAMPAIGN listings always stay on the legacy entities API.
   */
  private registerDriveNodes(root: EntityItem): void {
    const visit = (node: EntityItem): void => {
      if (node.entityType === PLATFORM_CHAT_ENTITY_TYPE.PROJECT && node.additionalProperties?.mode === 'drive') {
        node.hasChildren = true;
        if (!this.driveNodeRegistry.has(node.entityUuid)) {
          this.driveNodeRegistry.set(node.entityUuid, null);
        }
      }
      node.children?.forEach(visit);
    };
    visit(root);
  }

  /**
   * Children of a drive-mode entity, sourced from the Drive service and mapped to `EntityInfo`.
   * `DEFAULT` sends no `sort` param, so children keep Drive's own persisted (manual) order.
   */
  private getDriveEntities(parentUuid: string): Observable<EntityInfo[]> {
    return this.resolveDriveDirectoryUuid(parentUuid).pipe(
      switchMap((directoryUuid: string | null) =>
        directoryUuid
          ? this.driveStructure
              .listChildren(directoryUuid, { includeFiles: true, sort: 'DEFAULT' })
              .pipe(map((items: DriveItem[]) => items.map((item) => this.mapDriveItemToEntity(item))))
          : of<EntityInfo[]>([])
      ),
      catchError(() => of([]))
    );
  }

  /**
   * Resolve the Drive directory whose children make up this node. Descendants carry their
   * `directoryUuid` in the registry; a drive project root resolves it from the project's
   * `driveDirectoryUuid` (the currently-loaded project, else a one-off fetch).
   */
  private resolveDriveDirectoryUuid(parentUuid: string): Observable<string | null> {
    const registered: string | null | undefined = this.driveNodeRegistry.get(parentUuid);
    if (registered) {
      return of(registered);
    }
    const project: Project | null = this.projectService.projectOnloaded ? this.projectService.project : null;
    if (project?.id === parentUuid && project.driveDirectoryUuid) {
      this.driveNodeRegistry.set(parentUuid, project.driveDirectoryUuid);
      return of(project.driveDirectoryUuid);
    }
    return this.projectService.getSingleProject(parentUuid).pipe(
      map((fetched: Project | null) => {
        const directoryUuid: string | null = fetched?.driveDirectoryUuid ?? null;
        if (directoryUuid) {
          this.driveNodeRegistry.set(parentUuid, directoryUuid);
        }
        return directoryUuid;
      }),
      catchError(() => of(null))
    );
  }

  /** Map a Drive item to an `EntityInfo`, registering directories so their children resolve on expand. */
  private mapDriveItemToEntity(item: DriveItem): EntityInfo {
    if (item.itemKind === 'FILE') {
      return {
        entityUuid: item.fileAliasUuid,
        entityType: PLATFORM_CHAT_ENTITY_TYPE.FILE,
        entityName: item.fileAliasDisplayName ?? item.fileName,
        hasChildren: false,
        children: null,
      };
    }
    const entityType: string = mapDirectoryKindToEntityType(item.directoryKind?.directoryKindName);
    this.driveNodeRegistry.set(item.elementUuid, item.directoryUuid);
    return {
      entityUuid: item.elementUuid,
      entityType,
      entityName: item.directoryDisplayName ?? item.directoryName,
      // VERSION directories always show the arrow (their file pages aren't reflected in the Drive
      // `hasChildren`); other descendants use the Drive service's own `hasChildren`.
      hasChildren: entityType === PLATFORM_CHAT_ENTITY_TYPE.VERSION ? true : item.hasChildren,
      children: null,
    };
  }

  generateEntityRbacToken(entityType: string, entityUuid: string, entityName?: string): Observable<string> {
    // Drive-mode signatures must carry projectId so the backend can scope them (mirrors
    // TaskMainPage.initializePlugin / getParticipants). The task-description conversation requests
    // its token with entityType 'TASK', which the backend resolves within the drive project.
    const project: Project | null = this.projectService.projectOnloaded ? this.projectService.project : null;
    const projectId: string | null =
      project?.mode === 'drive' && (entityType === 'TASK' || DRIVE_PROJECT_SCOPED_ENTITY_TYPES.has(entityType))
        ? (project?.id ?? null)
        : null;
    return this.rbacSignature.requestSignature(entityType, entityUuid, entityName, projectId).pipe(
      map(({ signature }: SignatureRequestResult) => {
        if (!signature?.access_token) {
          throw new Error('No access_token in signature response');
        }
        return signature.access_token;
      })
    );
  }
}
